import {
  useListProjectServicesQuery,
  V1ProjectPhaseResource,
  V1ProjectResourceResource,
  V1ProjectSubserviceResource,
  V1ServiceCategoryResource,
  V1ServiceResource,
  V1ProjectLocationResource,
} from "@generated";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { ProjectService } from "../../types";
import { getResourceName } from "../../../Projects/ServicesV2/common";

const useListProjectServices = ({ project_id, service_type }) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  let customError: string | null = null; // Declare customError

  const { data, error, isLoading, refetch, isFetching } =
    useListProjectServicesQuery({
      slug: accountSlug,
      filter: {
        project: project_id,
        "service-type": service_type,
      },
      include: [
        "service",
        "project-phase",
        "service-category",
        "project-resource",
        "project-subservices",
        //@ts-ignore
        "project-subservices.project-resource",
        "project-location",
      ],
    });

  let services: ProjectService[] = [];

  if (data?.data) {
    try {
      const included = data?.included;
      services = data.data.map((service, i) => {
        if (!service.id) {
          throw new Error("Missing service.id");
        }
        if (!service.attributes?.name) {
          throw new Error("Missing service.attributes.name");
        }
        const standard = included?.find(
          (item) =>
            item.type === "services" &&
            item.id === service?.relationships?.service?.data?.id
        ) as V1ServiceResource;
        const phase = included?.find(
          (item) =>
            item.type === "project-phases" &&
            item.id === service?.relationships?.["project-phase"]?.data?.id
        ) as V1ProjectPhaseResource;
        const category = included?.find(
          (item) =>
            item.type === "service-categories" &&
            item.id == service?.relationships?.["service-category"]?.data?.id
        ) as V1ServiceCategoryResource;
        const resource = included?.find(
          (item) =>
            item.type === "project-resources" &&
            item.id == service?.relationships?.["project-resource"]?.data?.id
        ) as V1ProjectResourceResource;
        const location = included?.find(
          (item) =>
            item.type === "project-locations" &&
            item.id == service?.relationships?.["project-location"]?.data?.id
        ) as V1ProjectLocationResource;

        const subservices =
          service.relationships?.["project-subservices"]?.data?.map(
            (subservice) => {
              const sub = included?.find(
                (item) =>
                  item.type === "project-subservices" &&
                  item.id === subservice.id
              ) as V1ProjectSubserviceResource;
              const subResource = included?.find(
                (item) =>
                  item.type === "project-resources" &&
                  item.id === sub?.relationships?.["project-resource"]?.data?.id
              ) as V1ProjectResourceResource;
              return {
                id: String(sub?.id),
                //@ts-ignore
                active: sub?.attributes?.active,
                type: "project-subservices",
                name: sub?.attributes?.name || " ",
                position: sub?.attributes?.position || 0,
                quantity: String(sub?.attributes?.quantity || 1),
                hours: String(sub?.attributes?.["override-hours"] || "0.0"),
                description: sub?.attributes?.["service-description"] || "",
                resource: subResource
                  ? {
                      value: `${subResource?.type}-${subResource.id}`,
                      label: getResourceName(subResource),
                    }
                  : resource
                  ? {
                      value: `${resource?.type}-${resource.id}`,
                      label: getResourceName(resource),
                    }
                  : undefined,
                languages: sub?.attributes?.languages || {},
                taskSource: sub?.attributes?.["task-source"],
                unitPrice: String(sub?.attributes?.["unit-price"] ?? "0.00"),
                unitCost: String(sub?.attributes?.["unit-cost"] ?? "0.00"),
                hourlyRate: String(sub?.attributes?.["hourly-rate"] ?? "0.00"),
                hourlyCost: String(sub?.attributes?.["hourly-cost"] ?? "0.00"),
              };
            }
          ) || [];
        return {
          id: String(service.id),
          type: "project-services",
          phase: String(phase?.id || ""),
          name: service.attributes?.name,
          position: service.attributes?.position || 0,
          description: service.attributes?.["service-description"] || "",
          quantity: String(service?.attributes?.quantity || 1),
          category: category?.id
            ? {
                value: String(category.id),
                label:
                  category.attributes?.["nested-name"] ||
                  category.attributes.name,
              }
            : undefined,
          hours: String(service.attributes?.["override-hours"] || "0.0"),
          resource: resource?.id
            ? {
                value: `${resource?.type}-${resource.id}`,
                label: getResourceName(resource),
              }
            : undefined,
          subservices: subservices.filter((s) => s.active === true),
          notes: service.attributes?.notes || [],
          standardId: String(standard?.id || ""),
          languages: service.attributes?.languages || {},
          serviceType: service.attributes?.["service-type"],
          taskSource: service?.attributes?.["task-source"],
          unitPrice: String(service?.attributes?.["unit-price"] ?? "0.00"),
          unitCost: String(service?.attributes?.["unit-cost"] ?? "0.00"),
          hourlyRate: String(service?.attributes?.["hourly-rate"] ?? "0.00"),
          hourlyCost: String(service?.attributes?.["hourly-cost"] ?? "0.00"),
          location: location?.id
            ? { value: String(location.id), label: location?.attributes?.name }
            : undefined,
        };
      });
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }
  return {
    fetchedServices:
      service_type == "professional_services"
        ? services.filter((s) => s.phase)
        : services,
    servicesLoading: isLoading,
    error: error ?? customError,
    refetchServices: refetch,
    servicesFetching: isFetching,
  };
};
export default useListProjectServices;
